import { useEffect, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useBasePath } from '../../../../lib/use-base-path';
import { useTranslation } from '../../../../lib/i18n';
import { BUCKETS, type BucketId } from '../../../../lib/home-map/map-layout';
import { TasksSummary } from '../../tasks/TasksSummary';
import { GoalsSummary } from '../../goals/GoalsSummary';
import { RoutinesSummary } from '../../routines/RoutinesSummary';
import { FilesSummaryCard } from '../../agents/FilesSummaryCard';
import { AppsSummaryCard } from '../../agents/AppsSummaryCard';
import { ContactsSummaryCard } from '../../agents/ContactsSummaryCard';
import styles from './MapTiles.module.css';
import { ACTIVITY_MAP_BI_PAGE } from '../../../../lib/activity-map-bi';

/**
 * The six entry tiles at the map's foot — Tasks · Goals · Routines · Files · Apps · Outreach — are THE SAME
 * summary tiles the Your Agents screen and a Space's dashboard draw (glyph beside the title, the status
 * counts as coloured icon badges, the drill-in chevron), not a third design. On desktop a tile is a TAB
 * (`onSelect` opens the section under the map); on a phone it navigates, exactly as it does on those
 * screens. A tile a particle just landed on bumps for a beat.
 */

export const TILE_ROUTES: Record<BucketId, string> = {
  tasks: '/tasks',
  goals: '/goals',
  routines: '/routines',
  files: '/files',
  apps: '/apps',
  messages: '/contacts',
};

/** A desktop tab under the map: a bucket's section, or the activity log (the timeline button's). */
export type MapTab = BucketId | 'activity';

/** Which tabs can open INLINE under the map on desktop — every tile, plus the activity log. */
export const INLINE_TABS: readonly MapTab[] = ['tasks', 'goals', 'routines', 'files', 'apps', 'messages', 'activity'];

const PAGE = ACTIVITY_MAP_BI_PAGE;

export function MapTiles({
  selected,
  onSelect,
  bumped,
  inline,
}: {
  /** The open desktop tab, if any. */
  selected: MapTab | null;
  onSelect: (bucket: BucketId) => void;
  /** Tiles that just received a particle — keyed by bucket, the bump's timestamp. */
  bumped: Partial<Record<BucketId, number>>;
  /** Desktop: inline tabs. Phone: navigation. */
  inline: boolean;
}) {
  const router = useRouter();
  const basePath = useBasePath();
  const { t } = useTranslation();
  // The tile logs its own `open_*` click under this page name; no second event here.
  const pick = (bucket: BucketId) => () => {
    if (inline && (INLINE_TABS as readonly string[]).includes(bucket)) onSelect(bucket);
    else void router.push(TILE_ROUTES[bucket], `${basePath}${TILE_ROUTES[bucket]}`);
  };
  const tile = (bucket: BucketId, node: ReactNode) => (
    <Bump key={bucket} at={bumped[bucket]} selected={selected === bucket} bucket={bucket}>
      {node}
    </Bump>
  );
  // On a phone every tile navigates (no `onSelect`), as on the Your Agents screen; the compact entry rows there.
  const sel = (bucket: BucketId) => (inline ? pick(bucket) : undefined);
  return (
    <div className={styles.row}>
      {BUCKETS.map((bucket) => {
        switch (bucket) {
          case 'tasks':
            return tile(bucket, <TasksSummary pageName={PAGE} entryRow={!inline} onSelect={sel(bucket)} />);
          case 'goals':
            return tile(bucket, <GoalsSummary pageName={PAGE} entryRow={!inline} onSelect={sel(bucket)} />);
          case 'routines':
            return tile(bucket, <RoutinesSummary pageName={PAGE} entryRow={!inline} onSelect={sel(bucket)} />);
          case 'files':
            return tile(bucket, <FilesSummaryCard pageName={PAGE} entryRow={!inline} onSelect={sel(bucket)} />);
          case 'apps':
            return tile(bucket, <AppsSummaryCard pageName={PAGE} entryRow={!inline} onSelect={sel(bucket)} />);
          case 'messages':
            // The map calls this tile OUTREACH (the screen it opens is the outreach book): the Your Agents
            // screen keeps calling the same tile Contacts.
            return tile(bucket, <ContactsSummaryCard pageName={PAGE} entryRow={!inline} onSelect={sel(bucket)} title={t('home.map.outreach')} />);
          default:
            return null;
        }
      })}
    </div>
  );
}

/** The tile's frame: the selected ring, and a one-beat bump when a particle lands (`at` = the landing's
 *  timestamp; each new one restarts the beat). */
function Bump({ at, selected, bucket, children }: { at?: number; selected: boolean; bucket: BucketId; children: ReactNode }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!at) return;
    setOn(true);
    const id = setTimeout(() => setOn(false), 600);
    return () => clearTimeout(id);
  }, [at]);
  return (
    <div
      className={`${styles.tile}${on ? ` ${styles.bump}` : ''}${selected ? ` ${styles.tileSelected}` : ''}`}
      data-bucket={bucket}
      aria-current={selected ? 'true' : undefined}
    >
      {children}
    </div>
  );
}
